/**
 * Move Player — immersive guided workout screen.
 *
 * Flow: ready(3s) → [exercise → rest] × N → complete.
 * Time-mode exercises auto-advance; reps-mode waits for SUCCESSIVO.
 * Videos are lazy-loaded (current) and the NEXT one is pre-warmed.
 * On completion the session is recorded and can be shared to the Feed
 * (same SharePostModal used by the Camminata).
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { completeMoveSession, createPost } from '../lib/api';
import { getExerciseVideoUrl, getExerciseThumbUrl, preloadExerciseVideo, localized } from '../lib/moveMedia';
import { keepAwake } from '../lib/native';
import { hapticMedium, hapticSuccess } from '../lib/haptics';
import SharePostModal from '../components/SharePostModal';
import MoveMusicBar from '../components/MoveMusicBar';
import {
  X, SkipForward, ChevronRight, Flame, Clock, Trophy, Zap,
  Play, Check, Loader2, VideoOff,
} from 'lucide-react';
import { toast } from 'sonner';

const formatTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

function ExerciseVideo({ exercise, isDark, language }) {
  const [failed, setFailed] = useState(false);
  const videoUrl = getExerciseVideoUrl(exercise);
  const thumbUrl = getExerciseThumbUrl(exercise);

  useEffect(() => { setFailed(false); }, [exercise?.exercise_id]);

  if (failed || !videoUrl) {
    return (
      <div
        className="w-full aspect-video rounded-2xl flex flex-col items-center justify-center gap-2 bg-black/40 overflow-hidden relative"
        data-testid="move-video-unavailable"
      >
        {thumbUrl && (
          <img
            src={thumbUrl}
            alt={localized(exercise?.name, language)}
            className="absolute inset-0 w-full h-full object-cover opacity-40"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <VideoOff className="w-8 h-8 text-white/70 relative z-10" />
        <p className="text-white/80 text-sm relative z-10">
          {/* i18n handled by caller-provided label via title attr; text below is translated in parent */}
        </p>
      </div>
    );
  }

  return (
    <video
      key={videoUrl}
      src={videoUrl}
      poster={thumbUrl || undefined}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      className="w-full aspect-video rounded-2xl object-cover bg-black"
      onError={() => setFailed(true)}
      data-testid="move-exercise-video"
    />
  );
}

export default function MovePlayer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { t, language } = useTranslation();

  const workout = location.state?.workout;
  const items = useMemo(() => workout?.items || [], [workout]);

  const [stage, setStage] = useState('ready');   // ready | exercise | rest | done
  const [index, setIndex] = useState(0);
  const [countdown, setCountdown] = useState(3);
  const [completed, setCompleted] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [exitConfirm, setExitConfirm] = useState(false);
  const [finalStats, setFinalStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareText, setShareText] = useState('');
  const [scopeOpen, setScopeOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const startTsRef = useRef(Date.now());
  const totalSecondsRef = useRef(0);
  const caloriesRef = useRef(0);
  const savedRef = useRef(false);

  const workoutName = workout?.title_key
    ? t(`move.workoutNames.${workout.title_key}`, t('move.todayWorkout', 'Move di oggi'))
    : localized(workout?.name, language) || t('move.title', 'Move');

  const current = items[index];
  const userWeight = parseFloat(user?.current_weight || user?.weight || 70) || 70;

  // Redirect if opened without a workout (e.g. page refresh)
  useEffect(() => {
    if (!workout) navigate('/app/move', { replace: true });
  }, [workout, navigate]);

  // Keep the screen awake for the whole session
  useEffect(() => {
    try { keepAwake.enable(); } catch (_) { /* noop */ }
    return () => { try { keepAwake.disable(); } catch (_) { /* noop */ } };
  }, []);

  // Pre-warm the NEXT exercise's video only (lazy loading strategy)
  useEffect(() => {
    const next = items[index + 1];
    if (next?.exercise) preloadExerciseVideo(next.exercise);
  }, [index, items]);

  // Countdown driver (ready / timed exercise / rest)
  useEffect(() => {
    if (stage === 'done') return undefined;
    if (stage === 'exercise' && current?.mode !== 'time') return undefined;
    if (countdown <= 0) return undefined;
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown, stage, current]);

  const enterExercise = useCallback((i) => {
    const it = items[i];
    setIndex(i);
    setStage('exercise');
    if (it?.mode === 'time') setCountdown(it.duration_seconds || 30);
  }, [items]);

  const addCalories = useCallback((it) => {
    const activeSecs = it.mode === 'time' ? (it.duration_seconds || 30) : (it.reps || 10) * 3;
    const factor = it.exercise?.calorie_factor || 4;
    caloriesRef.current += (activeSecs / 60) * factor * (userWeight / 70);
  }, [userWeight]);

  const finish = useCallback(async (completedCount, skippedCount) => {
    totalSecondsRef.current = Math.round((Date.now() - startTsRef.current) / 1000);
    setStage('done');
    hapticSuccess();
    if (savedRef.current) return;
    savedRef.current = true;
    setSaving(true);
    try {
      const res = await completeMoveSession({
        workout_key: workout?.workout_key || 'move',
        workout_name: workoutName,
        total_seconds: totalSecondsRef.current,
        exercises_total: items.length,
        exercises_completed: completedCount,
        exercises_skipped: skippedCount,
        calories: Math.round(caloriesRef.current),
      });
      setFinalStats(res.data?.stats || null);
    } catch (_) { /* stats stay null — screen still shows local data */ }
    setSaving(false);
  }, [workout, workoutName, items.length]);

  const advance = useCallback((didComplete) => {
    const it = items[index];
    let c = completed, s = skipped;
    if (didComplete) { c += 1; setCompleted(c); addCalories(it); }
    else { s += 1; setSkipped(s); }

    if (index + 1 >= items.length) {
      finish(c, s);
      return;
    }
    const rest = it?.rest_seconds || 0;
    if (didComplete && rest > 0) {
      setStage('rest');
      setCountdown(rest);
    } else {
      enterExercise(index + 1);
    }
  }, [items, index, completed, skipped, addCalories, finish, enterExercise]);

  // Auto-advance when a timed exercise or rest reaches zero
  useEffect(() => {
    if (countdown > 0) return;
    if (stage === 'ready') { enterExercise(0); return; }
    if (stage === 'rest') { enterExercise(index + 1); return; }
    if (stage === 'exercise' && current?.mode === 'time') advance(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, stage]);

  const handleNext = () => { hapticMedium(); advance(true); };
  const handleSkip = () => { hapticMedium(); advance(false); };

  const openShare = () => {
    const def = t('move.complete.shareDefault', '🏋️ Ho completato l\'allenamento "{name}"! {exercises} esercizi in {duration}, circa {calories} kcal bruciate. 💪')
      .replace('{name}', workoutName)
      .replace('{exercises}', String(completed))
      .replace('{duration}', formatTime(totalSecondsRef.current))
      .replace('{calories}', String(Math.round(caloriesRef.current)));
    setShareText(def);
    setShareOpen(true);
  };

  const handlePublish = async (challengeIds) => {
    setPublishing(true);
    try {
      const payload = {
        post_type: 'esercizio',
        text: shareText,
        data: {
          workout_name: workoutName,
          duration_seconds: totalSecondsRef.current,
          exercises_completed: completed,
          exercises_total: items.length,
          calories: Math.round(caloriesRef.current),
          streak: finalStats?.streak,
          source: 'move',
        },
      };
      if (Array.isArray(challengeIds) && challengeIds.length > 0) payload.challenge_ids = challengeIds;
      await createPost(payload);
      navigate('/app/feed');
    } catch (_) {
      toast.error(t('move.error', 'Errore di caricamento. Riprova.'));
      setPublishing(false);
      setScopeOpen(false);
    }
  };

  if (!workout) return null;

  const ex = current?.exercise;
  const nextItem = items[index + 1];

  // ── COMPLETION SCREEN ────────────────────────────────────────────
  if (stage === 'done') {
    const doneStats = [
      { key: 'time', icon: Clock, value: formatTime(totalSecondsRef.current), label: t('move.complete.totalTime', 'Tempo totale') },
      { key: 'calories', icon: Flame, value: Math.round(caloriesRef.current), label: t('move.complete.calories', 'Calorie stimate') },
      { key: 'workouts', icon: Trophy, value: finalStats?.workouts_completed ?? '—', label: t('move.complete.workouts', 'Workout completati') },
      { key: 'streak', icon: Zap, value: finalStats?.streak ?? '—', label: t('move.complete.streak', 'Serie consecutiva') },
    ];
    return (
      <div
        className={`fixed inset-0 z-[200] flex flex-col overflow-y-auto ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="move-complete-screen"
      >
        <div className="flex-1 flex flex-col items-center justify-center px-6 pt-10">
          <div className="move-success-pop w-24 h-24 rounded-full flex items-center justify-center mb-6"
            style={{ background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)' }}>
            <Check className="w-12 h-12 text-white" strokeWidth={3} />
          </div>
          <h1 className={`font-display text-3xl mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('move.complete.congrats', 'Complimenti!')}
          </h1>
          <p className={`text-sm mb-8 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            {t('move.complete.subtitle', 'Hai completato il workout')} · {workoutName}
          </p>
          <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
            {doneStats.map(({ key, icon: Icon, value, label }) => (
              <div key={key}
                className={`rounded-2xl p-4 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-100'}`}
                data-testid={`move-complete-stat-${key}`}>
                <Icon className="w-5 h-5 text-[#E8334A] mb-2" />
                <p className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {saving && (key === 'workouts' || key === 'streak') ? <Loader2 className="w-5 h-5 animate-spin" /> : value}
                </p>
                <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="px-6 pb-10 space-y-3 max-w-sm w-full mx-auto">
          <button onClick={openShare}
            className="w-full py-4 rounded-2xl font-display text-lg text-white active:scale-[0.98] transition-transform"
            style={{ background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)' }}
            data-testid="move-share-btn">
            {t('move.complete.share', 'Condividi')}
          </button>
          <button onClick={() => navigate('/app/dashboard')}
            className={`w-full py-4 rounded-2xl font-semibold ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-800'}`}
            data-testid="move-home-btn">
            {t('move.complete.home', 'Torna alla Home')}
          </button>
        </div>

        {/* Share text editor */}
        {shareOpen && (
          <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4"
            onClick={() => !publishing && setShareOpen(false)}>
            <div className={`w-full max-w-sm rounded-3xl p-5 ${isDark ? 'bg-[#111827] border border-white/10' : 'bg-white shadow-2xl'}`}
              onClick={(e) => e.stopPropagation()} data-testid="move-share-modal">
              <h3 className={`font-display text-lg mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('move.complete.editText', 'Testo del post')}
              </h3>
              <textarea
                value={shareText}
                onChange={(e) => setShareText(e.target.value)}
                rows={4}
                className={`w-full rounded-2xl p-3 text-sm resize-none outline-none ${
                  isDark ? 'bg-white/5 text-white border border-white/10' : 'bg-gray-50 text-gray-900 border border-gray-200'
                }`}
                data-testid="move-share-text-input"
              />
              <div className="flex gap-2 mt-4">
                <button onClick={() => setShareOpen(false)} disabled={publishing}
                  className={`flex-1 py-3 rounded-2xl font-medium ${isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'}`}
                  data-testid="move-share-cancel-btn">
                  {t('move.complete.cancel', 'Annulla')}
                </button>
                <button onClick={() => { setShareOpen(false); setScopeOpen(true); }} disabled={publishing || !shareText.trim()}
                  className="flex-1 py-3 rounded-2xl font-semibold text-white bg-[#E8334A]"
                  data-testid="move-share-publish-btn">
                  {t('move.complete.publish', 'Pubblica')}
                </button>
              </div>
            </div>
          </div>
        )}

        <SharePostModal
          open={scopeOpen}
          publishing={publishing}
          onClose={() => setScopeOpen(false)}
          onConfirm={handlePublish}
        />
      </div>
    );
  }

  // ── READY / EXERCISE / REST ──────────────────────────────────────
  // Fullscreen overlay (like the Camminata) — covers the bottom nav.
  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col bg-[#0D1424]"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="move-player"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pt-safe-4 pt-4 pb-2">
        <div className="min-w-0">
          <p className="text-white font-display text-base truncate">{workoutName}</p>
          <p className="text-white/60 text-xs" data-testid="move-progress-label">
            {t('move.player.exerciseOf', 'Esercizio {current} di {total}')
              .replace('{current}', String(Math.min(index + 1, items.length)))
              .replace('{total}', String(items.length))}
          </p>
        </div>
        <button onClick={() => setExitConfirm(true)}
          className="p-2 rounded-full bg-white/10 text-white" data-testid="move-exit-btn">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-4 mb-3">
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#E8334A] to-[#F59E0B] transition-all duration-500"
            style={{ width: `${(index / Math.max(1, items.length)) * 100}%` }} />
        </div>
      </div>

      {/* Background music (admin-uploaded tracks; hidden when none) */}
      <MoveMusicBar />

      {stage === 'ready' ? (
        <div className="flex-1 flex flex-col items-center justify-center" data-testid="move-ready-screen">
          <p className="text-white/70 text-lg mb-4">{t('move.player.getReady', 'Preparati')}</p>
          <p className="font-display text-white text-7xl move-count-pop" key={countdown}>{countdown}</p>
          {items[0]?.exercise && (
            <p className="text-white/60 text-sm mt-6">
              {t('move.player.restNext', 'Prossimo: {name}').replace('{name}', localized(items[0].exercise.name, language))}
            </p>
          )}
        </div>
      ) : stage === 'rest' ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6" data-testid="move-rest-screen">
          <p className="text-white/70 text-lg mb-2">{t('move.player.rest', 'Riposo')}</p>
          <p className="font-display text-white text-7xl mb-6 move-count-pop" key={countdown}>{countdown}</p>
          {nextItem?.exercise && (
            <p className="text-white/70 text-sm mb-6 text-center">
              {t('move.player.restNext', 'Prossimo: {name}').replace('{name}', localized(nextItem.exercise.name, language))}
            </p>
          )}
          <button onClick={() => { setCountdown(0); }}
            className="px-8 py-3 rounded-2xl bg-white/10 text-white font-semibold"
            data-testid="move-rest-skip-btn">
            {t('move.player.skipRest', 'Salta riposo')}
          </button>
        </div>
      ) : (
        <div className="flex-1 flex flex-col px-4 overflow-y-auto">
          <ExerciseVideo exercise={ex} isDark={isDark} language={language} />

          <div className="mt-4 flex-1">
            <h2 className="font-display text-white text-2xl mb-1" data-testid="move-exercise-name">
              {localized(ex?.name, language)}
            </h2>
            <p className="text-white/70 text-sm leading-relaxed" data-testid="move-exercise-description">
              {localized(ex?.description, language)}
            </p>

            <div className="mt-4 flex items-center justify-center py-3">
              {current?.mode === 'time' ? (
                <div className="text-center" data-testid="move-exercise-timer">
                  <p className="font-display text-white text-6xl">{formatTime(countdown)}</p>
                </div>
              ) : (
                <div className="text-center" data-testid="move-exercise-reps">
                  <p className="font-display text-white text-6xl">{current?.reps || 10}</p>
                  <p className="text-white/60 text-sm uppercase tracking-wide">{t('move.player.reps', 'ripetizioni')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Big action buttons */}
          <div className="pb-8 pt-2 space-y-3">
            <button onClick={handleNext}
              className="w-full py-4 rounded-2xl font-display text-lg text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
              style={{ background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)' }}
              data-testid="move-next-btn">
              {t('move.player.next', 'SUCCESSIVO')}
              <ChevronRight className="w-5 h-5" />
            </button>
            <button onClick={handleSkip}
              className="w-full py-3.5 rounded-2xl font-semibold text-white/80 bg-white/10 flex items-center justify-center gap-2"
              data-testid="move-skip-btn">
              <SkipForward className="w-4 h-4" />
              {t('move.player.skip', 'SALTA')}
            </button>
          </div>
        </div>
      )}

      {/* Exit confirm */}
      {exitConfirm && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4">
          <div className={`w-full max-w-sm rounded-3xl p-6 ${isDark ? 'bg-[#111827] border border-white/10' : 'bg-white shadow-2xl'}`}
            data-testid="move-exit-confirm">
            <h3 className={`font-display text-lg mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('move.player.exitConfirmTitle', 'Vuoi uscire?')}
            </h3>
            <p className={`text-sm mb-5 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              {t('move.player.exitConfirmBody', 'I progressi di questo workout andranno persi.')}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setExitConfirm(false)}
                className={`flex-1 py-3 rounded-2xl font-semibold ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-800'}`}
                data-testid="move-exit-cancel-btn">
                {t('move.player.exitConfirmNo', 'Continua')}
              </button>
              <button onClick={() => navigate('/app/move')}
                className="flex-1 py-3 rounded-2xl font-semibold text-white bg-[#E8334A]"
                data-testid="move-exit-confirm-btn">
                {t('move.player.exit', 'Esci')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
